import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LottieActions from "../actions/LottieActions.js";
import useStoreState from "../components/providers/useStoreState.jsx";
import LottieStore from "../stores/LottieStore.js";
import TransformerStore from "../stores/TransformerStore.js";
import { resolveBackgroundColor } from "../utils/LottieStyleResolver.js";
import { resizeLottieCanvas } from "../utils/LottieUtils.js";
import useLottieInstance from "./useLottieInstance.jsx";

const RESIZE_DEBOUNCE_MS = 150;

/**
 * Orchestrates DotLottie playback control, settings synchronization, resize handling,
 * and store subscriptions. Delegates instance lifecycle to useLottieInstance.
 *
 * @param {Object} params
 * @param {Object} params.content - Immutable Map of Lottie content configuration.
 * @param {Object} params.elemProps - Immutable Map of element properties (dimensions).
 * @param {string} params.id - The element ID.
 * @param {string} params.layout - The current layout (desktop/mobile).
 * @param {boolean} params.isSelected - Whether the element is currently selected.
 * @returns {{ containerRef: React.RefObject, isPlaying: boolean, togglePlayPause: Function, isLoading: boolean }}
 */
const useLottiePlayer = ({ content, elemProps, id, layout, isSelected }) => {
  const { containerRef, instance, isLoading } = useLottieInstance({
    content,
    elemProps,
    id,
    layout,
  });

  const [isPlaying, setIsPlayingRaw] = useState(false);
  /** Tracks play state for synchronous reads in effects and callbacks like togglePlayPause.
   *  Dual-tracked with isPlaying state — ref for stale-closure safety, state for re-renders. */
  const isPlayingRef = useRef(false);
  /** Defers a play toggle until the DotLottie instance becomes available.
   *  Must be a ref — changing it must NOT trigger re-renders or re-fire effects. */
  const pendingToggleRef = useRef(false);
  /** Tracks whether a non-looping animation has reached its end naturally.
   *  Used to trigger replay-on-prop-change. Must NOT trigger re-renders. */
  const hasCompletedRef = useRef(false);
  /** Tracks the previous transforming state to detect the "transform just ended" transition
   *  for immediate (non-debounced) resize. Classic previous-value ref pattern. */
  const wasTransformingRef = useRef(false);
  /** Read by the settings sync effect to check selection state without adding isSelected to its
   *  deps — avoids re-firing the effect on selection changes (which would conflict with the
   *  dedicated auto-play effect that handles selection transitions). */
  const isSelectedRef = useRef(isSelected);
  isSelectedRef.current = isSelected;

  const loop = content?.get("loop", 1) ?? 1;
  const speed = content?.get("speed", 1) ?? 1;
  const mode = content?.get("mode", "forward") ?? "forward";
  const bgColor = content?.get("backgroundColor");
  const elementWidth = elemProps?.get("w", 0) ?? 0;
  const elementHeight = elemProps?.get("h", 0) ?? 0;

  const toggleRequestId = useStoreState(LottieStore, () =>
    LottieStore.getToggleRequestId(id, layout),
  );
  const isTransforming = useStoreState(TransformerStore, () =>
    TransformerStore.getTransformingStatus(),
  );

  /**
   * Updates local playing state and dispatches to LottieStore.
   * Accepts elementId and elementLayout at call time to avoid prop-mirror refs.
   *
   * @param {boolean} playing - Whether the animation is playing.
   * @param {string} elementId - The element ID.
   * @param {string} elementLayout - The current layout (desktop/mobile).
   */
  const setPlayingState = useCallback((playing, elementId, elementLayout) => {
    isPlayingRef.current = playing;
    setIsPlayingRaw(playing);
    if (elementId && elementLayout) {
      LottieActions.setPlayingState(elementId, elementLayout, playing);
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    if (!instance) {
      pendingToggleRef.current = true;
      return;
    }

    if (isPlayingRef.current) {
      instance.pause();
      setPlayingState(false, id, layout);
    } else {
      instance.play();
      setPlayingState(true, id, layout);
    }
  }, [instance, setPlayingState, id, layout]);

  // Register complete event handler, re-registers when loop changes so closure stays current.
  useEffect(() => {
    if (!instance) return;

    const handler = () => {
      if (!loop) {
        hasCompletedRef.current = true;
        setPlayingState(false, id, layout);
      }
    };

    instance.addEventListener("complete", handler);
    return () => instance.removeEventListener("complete", handler);
  }, [instance, loop, setPlayingState, id, layout]);

  // Sync animation settings (speed, mode, loop, background) to the live DotLottie instance.
  useEffect(() => {
    if (!instance) return;

    instance.setSpeed(speed);
    instance.setMode(mode);
    instance.setLoop(loop);

    const resolved = resolveBackgroundColor(bgColor) || "transparent";
    const canvas = containerRef.current?.querySelector("canvas");
    if (canvas) canvas.style.backgroundColor = resolved;
    instance.setBackgroundColor(resolved);

    if (isPlayingRef.current) {
      instance.play();
    } else if (hasCompletedRef.current && isSelectedRef.current) {
      hasCompletedRef.current = false;
      instance.setFrame(0);
      instance.play();
      setPlayingState(true, id, layout);
    }
  }, [instance, loop, speed, mode, bgColor, setPlayingState, containerRef, id, layout]);

  // Auto-play animation when selected, pause when deselected.
  useEffect(() => {
    if (!instance || pendingToggleRef.current) return;

    if (isSelected) {
      if (hasCompletedRef.current) {
        hasCompletedRef.current = false;
        instance.setFrame(0);
      }
      instance.play();
      setPlayingState(true, id, layout);
    } else if (isPlayingRef.current) {
      instance.pause();
      setPlayingState(false, id, layout);
    }
  }, [instance, isSelected, setPlayingState, id, layout]);

  // Process pending toggle once instance becomes available.
  useEffect(() => {
    if (!instance) return;
    if (pendingToggleRef.current) {
      pendingToggleRef.current = false;
      instance.play();
      setPlayingState(true, id, layout);
    }
  }, [instance, setPlayingState, id, layout]);

  // Reset play-state bookkeeping when the animation reloads.
  // biome-ignore lint: instance is a change-detection dep that triggers the reset
  useEffect(() => {
    hasCompletedRef.current = false;
  }, [instance]);

  const prevToggleIdRef = useRef(toggleRequestId);
  useEffect(() => {
    if (toggleRequestId !== prevToggleIdRef.current) {
      prevToggleIdRef.current = toggleRequestId;
      togglePlayPause();
    }
  }, [toggleRequestId, togglePlayPause]);

  // Debounced resize on dimension change; immediate resize when a transform operation ends.
  const handleResize = useCallback(() => {
    if (!instance || !containerRef.current) return;

    const canvas = containerRef.current.querySelector("canvas");
    if (!canvas) return;

    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      resizeLottieCanvas(canvas, rect.width, rect.height, containerRef.current.ownerDocument);
    }

    if (typeof instance.resize === "function") {
      instance.resize();
    }
  }, [instance, containerRef]);

  const debouncedResize = useMemo(
    () => _.debounce(handleResize, RESIZE_DEBOUNCE_MS),
    [handleResize],
  );

  // biome-ignore lint: elementWidth, elementHeight are change-detection deps that trigger resize
  useEffect(() => {
    if (wasTransformingRef.current && !isTransforming) {
      debouncedResize.cancel();
      handleResize();
    } else {
      debouncedResize();
    }
    wasTransformingRef.current = isTransforming;

    return () => debouncedResize.cancel();
  }, [elementWidth, elementHeight, isTransforming, debouncedResize, handleResize]);

  return { containerRef, isPlaying, togglePlayPause, isLoading };
};

export default useLottiePlayer;
